import {
  Box,
  Button,
  Typography,
  FormControl,
  FormHelperText,
  OutlinedInput,
  useMediaQuery,
  Grid,
  Alert,
  CircularProgress,
  Link,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import cover from "../LoginPage/covers.jpg";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link as RouterLink } from "react-router-dom";
import ThemeDefaultComponent from "../../components/ThemeDefaultComponent/ThemeDefaultComponent";
import emailjs from "@emailjs/browser";

// ─────────────────────────────────────────────────────────────────────────────
// EmailJS Configuration
// 1. Create a free account at https://www.emailjs.com
// 2. Add an Email Service (Gmail, SMTP, etc.) → copy the Service ID
// 3. Create an Email Template with variables: {{from_email}}, {{alasan}}, {{timestamp}}
//    Set the recipient to: support@antaralogistic.com
//    Copy the Template ID
// 4. Go to Account → Public Key and copy it
// Then replace the three constants below:
// ─────────────────────────────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || "YOUR_SERVICE_ID";
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "YOUR_TEMPLATE_ID";
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "YOUR_PUBLIC_KEY";

interface IDeleteAccountInputs {
  email: string;
  alasan: string;
}

const Schema = yup
  .object({
    email: yup
      .string()
      .email("Format email tidak valid")
      .required("Kolom wajib diisi"),
    alasan: yup
      .string()
      .required("Kolom wajib diisi")
      .min(10, "Alasan minimal 10 karakter"),
  })
  .required();

const DeleteAccountPage = () => {
  const theme = useTheme();
  const isTabletScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isLaptopScreen = useMediaQuery(theme.breakpoints.up("md"));

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<IDeleteAccountInputs>({
    resolver: yupResolver(Schema),
    defaultValues: {
      email: "",
      alasan: "",
    },
  });

  const onSubmit = async (data: IDeleteAccountInputs) => {
    setIsLoading(true);
    setIsError(false);

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_email: data.email,
          alasan: data.alasan,
          timestamp: new Date().toLocaleString("id-ID", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
        EMAILJS_PUBLIC_KEY
      );
      setIsSuccess(true);
      reset();
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeDefaultComponent>
      <Box sx={{ backgroundColor: "white" }}>
        <Box
          sx={{
            minHeight: "100vh",
            minWidth: "100vw",
            position: "absolute",
            top: "50%",
            left: isTabletScreen ? "50%" : "25%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "#FFF",
            display: "flex",
            justifyContent: "center",
            borderRadius: 1,
            paddingX: 2,
            paddingY: 2,
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            paddingY={3}
            paddingX={isTabletScreen ? 2 : 4}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "inherit",
            }}
          >
            <form
              style={{
                width: "100%",
                maxWidth: "500px",
                minWidth: "200px",
              }}
              onSubmit={handleSubmit(onSubmit)}
            >
              {/* Logo */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  marginBottom: "24px",
                }}
              >
                <img
                  src="/images/antara-logo.png"
                  width="150px"
                  height="50px"
                  style={{ objectFit: "contain" }}
                  alt="Antara Logistic"
                />
              </Box>

              {/* Title */}
              <Box
                sx={{
                  width: "fit-content",
                  marginTop: 3,
                  marginBottom: 4,
                }}
              >
                <Typography variant="h4" fontWeight="bold" color="#000">
                  Hapus Akun
                </Typography>
                <Typography variant="body2" color="#a6a6a6">
                  Ajukan permintaan penghapusan akun Antara mobile Anda
                </Typography>
              </Box>

              {/* Success Alert */}
              {isSuccess && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Permintaan berhasil dikirim. Tim kami akan menghubungi Anda
                  melalui email dalam 1–3 hari kerja.
                </Alert>
              )}

              {/* Error Alert */}
              {isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Gagal mengirim permintaan. Silakan coba lagi atau hubungi{" "}
                  <strong>support@antaralogistic.com</strong> secara langsung.
                </Alert>
              )}

              {!isSuccess && (
                <>
                  <Grid container spacing={1}>
                    {/* Email Field */}
                    <Grid item xs={12}>
                      <Typography variant="body2">Email Akun</Typography>
                      <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                          <FormControl
                            fullWidth
                            variant="outlined"
                            error={Boolean(errors.email)}
                            sx={{ maxWidth: "500px" }}
                          >
                            <OutlinedInput
                              id="email"
                              type="email"
                              autoComplete="email"
                              autoFocus={isLaptopScreen}
                              placeholder="Contoh: budi1992@gmail.com"
                              size="small"
                              {...field}
                            />
                            <FormHelperText>
                              {errors.email ? errors.email.message : " "}
                            </FormHelperText>
                          </FormControl>
                        )}
                      />
                    </Grid>

                    {/* Reason Field */}
                    <Grid item xs={12}>
                      <Typography variant="body2">Alasan Penghapusan</Typography>
                      <Controller
                        name="alasan"
                        control={control}
                        render={({ field }) => (
                          <FormControl
                            fullWidth
                            variant="outlined"
                            error={Boolean(errors.alasan)}
                            sx={{ maxWidth: "500px" }}
                          >
                            <OutlinedInput
                              id="alasan"
                              multiline
                              rows={4}
                              placeholder="Tuliskan alasan Anda ingin menghapus akun..."
                              {...field}
                            />
                            <FormHelperText>
                              {errors.alasan ? errors.alasan.message : " "}
                            </FormHelperText>
                          </FormControl>
                        )}
                      />
                    </Grid>
                  </Grid>

                  {/* Disclaimer */}
                  <Box sx={{ mt: 0.5, mb: 2 }}>
                    <Typography variant="caption" color="#a6a6a6">
                      Dengan mengajukan permintaan ini, akun beserta seluruh
                      data Anda akan dihapus secara permanen setelah verifikasi
                      oleh tim kami.
                    </Typography>
                  </Box>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    fullWidth
                    size="large"
                    variant="contained"
                    color="secondary"
                    disabled={isLoading}
                    sx={{ marginTop: 1, maxWidth: "500px" }}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "KIRIM PERMINTAAN"
                    )}
                  </Button>
                </>
              )}

              {/* Back to Login */}
              <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
                <Link
                  component={RouterLink}
                  to="/"
                  variant="body2"
                  underline="hover"
                  sx={{ cursor: "pointer" }}
                >
                  Kembali ke Halaman Masuk
                </Link>
              </Box>
            </form>
          </Box>
        </Box>

        {/* Right Panel — Cover Image (desktop only) */}
        {isTabletScreen === false ? (
          <Box
            sx={{
              minHeight: "100vh",
              minWidth: "50vw",
              position: "absolute",
              top: "49.9%",
              left: "75%",
              transform: "translate(-50%, -50%)",
              backgroundColor: "white",
              display: "flex",
              backgroundImage: `url(${cover})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "top right",
              backgroundSize: "cover",
            }}
          />
        ) : null}
      </Box>
    </ThemeDefaultComponent>
  );
};

export default DeleteAccountPage;
